// The Mounts tab (in the character window): your mounts and their perks, which one you call with R, trading
// for new ones at the stable (Town Gate), and the airship: chipping in at the Sky Dock and boarding it.
import { AIRSHIP_COST, MOUNTS } from '../../../shared/mounts.js';
import { itemDef } from '../../../shared/items.js';
import type { MountsView } from '../../../shared/protocol.js';
import { itemIcon } from '../render/items.js';

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; }
function button(text: string, cls: string, fn: () => void, disabled = false, title = ''): HTMLButtonElement { const b = el('button', cls, text); b.type = 'button'; b.disabled = disabled; if (title) b.title = title; b.addEventListener('click', fn); return b; }

export interface MountsHost {
  view: MountsView | null;
  /** How many of an item you carry, and your gold. */
  have(def: string): number; gold(): number;
  atStable(): boolean; atDock(): boolean; myName(): string;
  pick(id: string): void; buy(id: string): void; fund(gold: number): void; board(): void;
  /** Draw a mount into a little canvas (side on). */
  paint(cv: HTMLCanvasElement, kind: number): void;
}

export function renderMounts(box: HTMLElement, h: MountsHost): void {
  const v = h.view;
  if (!v) { box.append(el('p', 'jb-note', 'Loading…')); return; }
  box.append(el('p', 'jb-note', h.atStable() ? 'You\'re at the stable: trade here for a new mount.' : 'Press R to ride (outside of town). New mounts are traded for at the stable, at the Town Gate.'));
  const list = el('div', 'mt-list');
  for (const m of MOUNTS) {
    const owned = v.owned.includes(m.id), on = v.steed === m.id;
    const card = el('div', 'mt-card' + (on ? ' on' : '') + (owned ? '' : ' locked'));
    const cv = el('canvas', 'mt-pic'); cv.width = 30; cv.height = 22; h.paint(cv, m.kind);
    const text = el('div', 'mt-text'), head = el('div', 'mt-head');
    head.append(el('b', '', m.name));
    if (on) head.append(el('span', 'jb-badge', 'Riding'));
    const tags = el('div', 'mt-tags');
    const tag = (t: string) => tags.append(el('span', 'mt-tag', t));
    if (m.speed !== 100) tag(`${m.speed > 100 ? '+' : ''}${m.speed - 100}% ground`);
    if (m.fly !== 100) tag(m.fly ? `${m.fly > 100 ? '+' : ''}${m.fly - 100}% flying` : 'no flying');
    if (m.seats > 1) tag('2 seats');
    if (m.sneak) tag('creatures ignore you');
    text.append(head, el('small', '', m.blurb), tags);
    const act = el('div', 'mt-act');
    if (owned) { if (!on) act.append(button('Ride this one', 'btn', () => h.pick(m.id))); }
    else if (m.cost) {
      const need = el('div', 'mt-need');
      for (const it of m.cost.items) {
        const d = itemDef(it.def), n = h.have(it.def), row = el('span', 'mt-item' + (n >= it.qty ? ' ok' : ''));
        const ic = itemIcon(it.def), c = el('canvas'); c.width = ic.width; c.height = ic.height; c.getContext('2d')!.drawImage(ic, 0, 0);
        row.append(c, document.createTextNode(`${Math.min(n, it.qty)}/${it.qty} ${d?.name ?? it.def}`)); row.title = d?.desc ?? ''; need.append(row);
      }
      need.append(el('span', 'mt-item' + (h.gold() >= m.cost.gold ? ' ok' : ''), `${m.cost.gold} gil`));
      const can = h.atStable() && m.cost.items.every(it => h.have(it.def) >= it.qty) && h.gold() >= m.cost.gold;
      act.append(need, button('Trade', 'btn' + (can ? ' primary' : ''), () => h.buy(m.id), !can, h.atStable() ? '' : 'Trade at the stable, at the Town Gate'));
    }
    card.append(cv, text, act); list.append(card);
  }
  box.append(list);
  // the airship
  const a = v.airship, ship = el('div', 'mt-ship');
  ship.append(el('h3', 'set-h', '⛵ Airship'));
  if (a.owned) {
    ship.append(el('p', 'mt-small', 'Your airship is moored at the Sky Dock (on the world map, east of town). Board it there with R: your party nearby comes aboard. It flies over everything, even the mountains. Press R over open ground to land.'));
    ship.append(button('Board', 'btn' + (h.atDock() ? ' primary' : ''), () => h.board(), !h.atDock(), h.atDock() ? '' : 'Go to the Sky Dock'));
  } else if (a.fund) {
    const mine = a.fund.ownerName === h.myName();
    ship.append(el('p', 'mt-small', mine ? `Build your own airship at the Sky Dock: ${AIRSHIP_COST} gil. Anyone in your party can chip in toward it.` : `Your party is building ${a.fund.ownerName}'s airship (the leader's): chip in at the Sky Dock. Everyone in the party rides it.`));
    const bar = el('div', 'sk-bar mt-bar'), fill = el('i'); fill.style.width = `${Math.round(a.fund.total / a.cost * 100)}%`; bar.append(fill);
    ship.append(bar, el('small', 'mt-small', `${a.fund.total} / ${a.cost} gil${a.fund.from.length ? ' · ' + a.fund.from.map(f => `${f.name} ${f.gold}`).join(', ') : ''}`));
    const row = el('div', 'row-btns'), left = a.cost - a.fund.total;
    for (const g of [25, 100, 250].filter(g => g < left).concat([left])) {
      const ok = h.atDock() && h.gold() >= g;
      row.append(button(g === left ? `Finish it (${g})` : `Chip in ${g}`, 'btn' + (ok && g === left ? ' primary' : ''), () => h.fund(g), !ok, h.atDock() ? (h.gold() < g ? 'Not enough gold' : '') : 'Chip in at the Sky Dock'));
    }
    ship.append(row);
  }
  box.append(ship);
}
